import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type { UserPreferences } from '@/types/content';
import { authCache } from '@/utils/authCache';
import { retryAuthOperation } from '@/utils/authRetry';
import { authMonitor } from '@/utils/authMonitor';

interface PersonalizationContextType {
  preferences: UserPreferences | null;
  updatePreferences: (updates: Partial<UserPreferences>) => Promise<void>;
  trackBehavior: (actionType: string, appName?: string, metadata?: any) => Promise<void>;
  isLoading: boolean;
}

const PersonalizationContext = createContext<PersonalizationContextType | undefined>(undefined);

export function PersonalizationProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionId] = useState(() => crypto.randomUUID());

  useEffect(() => {
    if (user) {
      loadPreferences();
    } else {
      setPreferences(null);
      setIsLoading(false);
    }
  }, [user]);

  const loadPreferences = async () => {
    authMonitor.logRequest();
    try {
      // Use cached auth with retry for 503/504 errors
      const authUser = await retryAuthOperation(() => authCache.getUser());
      
      if (!authUser) {
        setIsLoading(false);
        return; // Not logged in - silent fail
      }

      // Retry database query with backoff
      const result = await retryAuthOperation(async () => {
        return await supabase
          .from('user_preferences')
          .select('*')
          .eq('user_id', authUser.id)
          .maybeSingle();
      });

      if (!result) {
        // Retry failed - use defaults
        if (user) {
          setPreferences({
            user_id: user.id,
            theme_preference: 'dark',
            dashboard_layout: 'grid',
            sidebar_collapsed: false,
            favorite_apps: [],
            pinned_apps: [],
          } as unknown as UserPreferences);
        }
        setIsLoading(false);
        return;
      }

      const { data, error } = result;
      if (error && error.code !== 'PGRST116') throw error;

      if (data) {
        setPreferences(data);
      } else {
        // Create default preferences
        const defaultPrefs = {
          user_id: authUser.id,
          theme_preference: 'dark',
          dashboard_layout: 'grid',
          sidebar_collapsed: false,
          favorite_apps: [],
          pinned_apps: [],
        };
        
        const { data: newPrefs, error: insertError } = await supabase
          .from('user_preferences')
          .insert(defaultPrefs)
          .select()
          .single();

        if (insertError) throw insertError;
        setPreferences(newPrefs);
      }
    } catch (error: any) {
      authMonitor.logError(error);
      console.error('Error loading preferences:', error);
      
      // Silent fallback to defaults - don't block UI
      if (user) {
        setPreferences({
          user_id: user.id,
          theme_preference: 'dark',
          dashboard_layout: 'grid',
          sidebar_collapsed: false,
          favorite_apps: [],
          pinned_apps: [],
        } as unknown as UserPreferences);
      }
      
      // Only show toast for unexpected errors (not 409/503/504)
      if (![409, 503, 504].includes(error?.status || error?.code)) {
        toast({
          title: "Using default preferences",
          description: "Your custom settings will load next time.",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const updatePreferences = async (updates: Partial<UserPreferences>) => {
    if (!user) return;

    try {
      // Check if preferences exist
      const { data: existing } = await supabase
        .from('user_preferences')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (existing) {
        // Update existing
        const { error } = await supabase
          .from('user_preferences')
          .update(updates)
          .eq('user_id', user.id);

        if (error) throw error;
      } else {
        // Insert new
        const { error } = await supabase
          .from('user_preferences')
          .insert({ ...updates, user_id: user.id });

        if (error) throw error;
      }

      setPreferences((prev) => (prev ? { ...prev, ...updates } : null));

      toast({
        title: "Preferences updated",
        description: "Your personalization settings have been saved.",
      });
    } catch (error: any) {
      console.error('Error updating preferences:', error);
      toast({
        title: "Error updating preferences",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const trackBehavior = async (actionType: string, appName?: string, metadata?: any) => {
    if (!user) return;

    try {
      // Use cached session
      const session = await authCache.getSession();
      if (!session) {
        console.warn('No active session, skipping behavior tracking');
        return;
      }

      await supabase.functions.invoke('track-user-behavior', {
        body: {
          action_type: actionType,
          app_name: appName,
          metadata: metadata || {},
          session_id: sessionId,
          device_type: /Mobile|Tablet/.test(navigator.userAgent) ? 'mobile' : 'desktop',
        },
      });
    } catch (error) {
      console.error('Error tracking behavior:', error);
      // Silent fail - don't interrupt user flow
    }
  };

  return (
    <PersonalizationContext.Provider value={{ preferences, updatePreferences, trackBehavior, isLoading }}>
      {children}
    </PersonalizationContext.Provider>
  );
}

export function usePersonalization() {
  const context = useContext(PersonalizationContext);
  if (!context) {
    throw new Error('usePersonalization must be used within PersonalizationProvider');
  }
  return context;
}